import React from 'react';
import styled from 'styled-components';
import { FlexContainer } from '../Components/UI/Containers';
import { Text } from '../Components/UI/Typography';
import PropTypes from 'prop-types';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.background} 0%, 
    ${props => props.theme.colors.backgroundSecondary || props.theme.colors.background} 100%
  );
  border-top: 1px solid ${props => props.theme.colors.border}30;
  margin-left: ${props => props.theme.spacing.xxl};
  margin-right: ${props => props.theme.spacing.xxl};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xxl} 0;
  margin-top: auto;
  backdrop-filter: blur(8px);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent, 
      ${props => props.theme.colors.border}60, 
      transparent
    );
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md} 0;
    margin-left: ${props => props.theme.spacing.md};
    margin-right: ${props => props.theme.spacing.md};
  }
`;

const FooterContent = styled(FlexContainer)`
  justify-content: space-between;
  align-items: flex-start;
  max-width: 1200px;
  margin: 0 auto;
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const BrandSection = styled(FlexContainer).attrs({
  direction: 'column',
  align: 'flex-start',
  gap: 'md'
})`
  flex: 1;
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const PoweredBySection = styled(FlexContainer).attrs({
  direction: 'column',
  align: 'flex-start',
  gap: 'sm'
})`
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.large};
  background: ${props => props.theme.colors.primary}05;
  border: 1px solid ${props => props.theme.colors.primary}15;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: ${props => props.theme.colors.primary}08;
    border-color: ${props => props.theme.colors.primary}25;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.theme.colors.primary}20;
  }
`;

const FooterLogo = styled.img`
  height: 28px;
  width: auto;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  
  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  }
`;

const CompanyInfo = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'xs'
})``;

const PoweredByText = styled(Text).attrs({
  size: 'xs',
  weight: 'medium'
})`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const CompanyName = styled(Text).attrs({
  size: 'sm',
  weight: 'semibold'
})`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily};
  letter-spacing: 0.5px;
`;

const ContactSection = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'sm',
  align: 'flex-end'
})`
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ContactTitle = styled(Text).attrs({
  size: 'sm',
  weight: 'semibold'
})`
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ContactInfo = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'xs',
  align: 'flex-end'
})`
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ContactText = styled(Text).attrs({
  size: 'xs'
})`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  transition: color 0.2s ease;
  cursor: default;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${props => props.theme.typography.fontSizes.xs};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateX(-2px);
  }
`;

const CopyrightSection = styled.div`
  border-top: 2px solid ${props => props.theme.colors.border}25;
  padding: ${props => props.theme.spacing.sm} 0;
  text-align: center;
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: ${props => props.theme.spacing.xs} auto 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent, 
      ${props => props.theme.colors.border}50, 
      transparent
    );
  }
  
  @media (max-width: 768px) {
    margin-top: ${props => props.theme.spacing.md};
    padding: ${props => props.theme.spacing.sm} 0;
  }
`;

const Copyright = styled(Text).attrs({
  size: 'xs'
})`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  opacity: 0.7;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-align: center;
`;

const Footer = ({ className, ...props }) => {
  const currentYear = new Date().getFullYear();
  
  // Memoized contact info to prevent unnecessary re-renders
  const contactInfo = React.useMemo(() => ({
    address: '145/3, Park Rd, Colombo 005, Sri Lanka',
    phone: '+94 76 817 3132',
    email: 'internal@sentinel.lk'
  }), []);

  return (
    <FooterContainer className={className} {...props}>
      <FooterContent>
        <BrandSection>
          <PoweredBySection>
            <LogoContainer>
              <FooterLogo 
                src="/assets/white-background-removebg-preview.png" 
                alt="Qualqem Logo" 
                loading="lazy"
              />
            </LogoContainer>
            <CompanyInfo>
              <PoweredByText>powered by</PoweredByText>
              <CompanyName>Sentinel Systems (Pvt) Ltd</CompanyName>
            </CompanyInfo>
          </PoweredBySection>
        </BrandSection>
        
        <ContactSection>
          <ContactTitle>Get in Touch</ContactTitle>
          <ContactInfo>
            <ContactText>{contactInfo.address}</ContactText>
            <ContactLink 
              href={`tel:${contactInfo.phone}`}
              aria-label="Call us"
            >
              {contactInfo.phone}
            </ContactLink>
            <ContactLink 
              href={`mailto:${contactInfo.email}`}
              aria-label="Email us"
            >
              {contactInfo.email}
            </ContactLink>
          </ContactInfo>
        </ContactSection>
      </FooterContent>

      <CopyrightSection>
        <Copyright>
          © {currentYear} All rights reserved.
        </Copyright>
      </CopyrightSection>
    </FooterContainer>
  );
};

// PropTypes for production safety
Footer.propTypes = {
  className: PropTypes.string
};

export default React.memo(Footer);